/**
 * Deriv partner attribution. A Deriv partner has two links (Partners dashboard, read live
 * 2026-10-12), both carrying their tracking token in `t`:
 *   - client referral link: t.deriv.link?t=X → home.deriv.com/dashboard/signup?t=X. The OAuth
 *     sign-up of the "Conectar" button takes the same token (`t` + utm_*), so an account created
 *     through it is the partner's at Deriv. Accounts that already exist keep their attribution.
 *   - partner referral link ("refer partners"): home.deriv.com/partners-app/signup?t=X — whoever
 *     signs up as a partner there becomes a sub-partner of X.
 */
export interface DerivAffiliateLink {
  token: string;
  utmCampaign: string | null;
  utmSource: string | null;
}

/** Tracking tokens are short case-sensitive codes (FCR5NAHYFZ5V); the legacy MyAffiliates ones start with "_". */
const TOKEN_RE = /^[A-Za-z0-9_-]{4,64}$/;

export type ParsedDerivLink = { ok: true; value: DerivAffiliateLink; kind: 'client' | 'partner' } | { ok: false; error: string };

/** A link pasted with text around it ("Meu link: https://…"): the part that carries the token. */
function linkPart(input: string): string {
  const t = input.trim();
  return /\s/.test(t) ? (/\S*[?&](?:t|affiliate_token|sidi|ca)=\S*/.exec(t)?.[0] ?? t) : t;
}

function toUrl(text: string): URL | null {
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`);
  } catch {
    return null;
  }
}

const clean = (v: string | null) => (v ?? '').replace(/[^A-Za-z0-9_.-]/g, '').slice(0, 64) || null;

/**
 * Reads what a partner pastes: their Deriv referral link (any form, with or without https://) or
 * just the tracking token. Short links of other services carry no token and are refused.
 */
export function parseDerivAffiliateLink(input: string): ParsedDerivLink {
  const text = linkPart(input);
  if (!text) return { ok: false, error: 'Cole o seu link de indicação da Deriv.' };
  if (TOKEN_RE.test(text) && !text.includes('.')) return { ok: true, value: { token: text, utmCampaign: null, utmSource: null }, kind: 'client' };
  const url = toUrl(text);
  if (!url || !/(^|\.)deriv\.(com|link|me|be)$/i.test(url.hostname)) {
    return { ok: false, error: 'Isso não parece um link da Deriv. Copie o link de indicação do painel de parceiros da Deriv, ou digite só o seu código (t=…).' };
  }
  const q = url.searchParams;
  const token = q.get('t') ?? q.get('affiliate_token') ?? q.get('sidi') ?? q.get('ca');
  if (!token || !TOKEN_RE.test(token)) {
    return { ok: false, error: 'Esse link não tem o seu código de parceiro (t=…). Copie o link completo do painel de parceiros da Deriv.' };
  }
  return {
    ok: true,
    value: { token, utmCampaign: clean(q.get('utm_campaign')), utmSource: clean(q.get('utm_source')) },
    kind: /partners-app/i.test(url.pathname) ? 'partner' : 'client',
  };
}

export const DERIV_PARTNER_LINK_IN_CLIENT_FIELD =
  'Esse é o link de cadastro de parceiros, não serve aqui. Cole o link de indicação de clientes (t.deriv.link?t=…) do painel de parceiros da Deriv.';
export const DERIV_NOT_PARTNER_LINK = 'Esse não é o link de cadastro de parceiros. Copie no painel de parceiros da Deriv o link “indicar parceiros” (partners-app/signup?t=…).';

/**
 * Reads a link for one of the two fields: 'client' (the referral link of the "Conectar" sign-up: an
 * affiliate's own, or the platform's) refuses a partner sign-up link; 'partner' (the platform's
 * partner referral link, for affiliates who become Deriv partners) takes only that one.
 */
export function parseDerivLinkFor(input: string, field: 'client' | 'partner'): ParsedDerivLink {
  const parsed = parseDerivAffiliateLink(input);
  if (field === 'partner') {
    if (!input.trim()) return { ok: false, error: 'Cole o link de cadastro de parceiros da Deriv.' };
    return parsed.ok && parsed.kind === 'partner' ? parsed : { ok: false, error: DERIV_NOT_PARTNER_LINK };
  }
  return parsed.ok && parsed.kind === 'partner' ? { ok: false, error: DERIV_PARTNER_LINK_IN_CLIENT_FIELD } : parsed;
}

/** The partner sign-up link to open from the affiliate panel (https, on Deriv, with the token), or null. */
export function derivPartnerSignupUrl(input: string | null | undefined): string | null {
  if (!input) return null;
  const parsed = parseDerivLinkFor(input, 'partner');
  if (!parsed.ok) return null;
  const url = toUrl(linkPart(input));
  return url?.protocol === 'https:' ? url.href : `https://home.deriv.com/partners-app/signup?t=${encodeURIComponent(parsed.value.token)}`;
}

/** Where partners generate their links. */
export const DERIV_PARTNERS_URL = 'https://home.deriv.com/partners-app';
